import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const WA_LINK = "https://wa.me/393296281756?text=Ciao%20Danielealoisi%2C%20vorrei%20un%20preventivo%2Fsopralluogo%20per%20infissi%20o%20lavorazioni%20in%20ferro.%20Zona%3A%20_____%20.%20Posso%20inviare%20alcune%20foto.";

const formSchema = z.object({
  name: z.string().trim().min(1, "Inserisci il tuo nome").max(100),
  phone: z.string().trim().min(6, "Inserisci un numero valido").max(20),
  zone: z.string().trim().min(1, "Inserisci la tua zona").max(100),
  service: z.string().min(1, "Seleziona un servizio"),
  message: z.string().trim().max(500).optional(),
});

type FormData = z.infer<typeof formSchema>;

const CTASection = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    zone: "",
    service: "",
    message: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = formSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FormData, string>> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof FormData;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const text = `Ciao Danielealoisi, sono ${encodeURIComponent(result.data.name)}.%0AZona: ${encodeURIComponent(result.data.zone)}%0AServizio: ${encodeURIComponent(result.data.service)}%0A${result.data.message ? `Note: ${encodeURIComponent(result.data.message)}` : ""}%0ATel: ${encodeURIComponent(result.data.phone)}`;
    window.open(`https://wa.me/393296281756?text=${text}`, "_blank");
    navigate(`/grazie?name=${encodeURIComponent(result.data.name)}&service=${encodeURIComponent(result.data.service)}`);
  };

  const inputClass =
    "w-full bg-transparent border border-foreground/60 rounded-sm px-4 py-3 text-foreground font-body text-sm placeholder:text-foreground/50 focus:outline-none focus:border-accent transition-colors duration-300";

  return (
    <section id="contact" ref={ref} className="bg-background py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.1] text-foreground">
            Richiedi un preventivo chiaro
          </h2>
          <p className="mt-6 text-muted-foreground font-body max-w-lg mx-auto">
            Compila il form e ti ricontatteremo. Se puoi, invia 2 foto su WhatsApp: velocizza la valutazione.
          </p>
        </motion.div>

        <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-5"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Nome e cognome *"
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClass}
                  maxLength={100}
                />
                {errors.name && <p className="mt-1 text-xs text-red-400 font-body">{errors.name}</p>}
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Telefono *"
                  value={formData.phone}
                  onChange={handleChange}
                  className={inputClass}
                  maxLength={20}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-400 font-body">{errors.phone}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  name="zone"
                  placeholder="Zona (es. Roma Est, Guidonia) *"
                  value={formData.zone}
                  onChange={handleChange}
                  className={inputClass}
                  maxLength={100}
                />
                {errors.zone && <p className="mt-1 text-xs text-red-400 font-body">{errors.zone}</p>}
              </div>
              <div>
                <label htmlFor="service-select" className="sr-only">Servizio</label>
                <select
                  id="service-select"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className={`${inputClass} ${!formData.service ? "text-muted-foreground/40" : ""}`}
                  aria-label="Seleziona un servizio"
                >
                  <option value="" disabled>Servizio *</option>
                  <option value="Infissi & Serramenti">Infissi & Serramenti</option>
                  <option value="Ferro & Sicurezza">Ferro & Sicurezza</option>
                  <option value="Riparazioni">Riparazioni</option>
                  <option value="Altro">Altro</option>
                </select>
                {errors.service && <p className="mt-1 text-xs text-red-400 font-body">{errors.service}</p>}
              </div>
            </div>

            <div>
              <textarea
                name="message"
                placeholder="Note aggiuntive (opzionale)"
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className={`${inputClass} resize-none`}
                maxLength={500}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
              <button
                type="submit"
                className="cta-button w-full sm:w-auto hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300"
              >
                Richiedi preventivo
              </button>
              <a
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 text-sm tracking-widest uppercase font-body bg-[#25D366] text-white rounded-sm hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300 w-full sm:w-auto"
              >
                Scrivi su WhatsApp
              </a>
            </div>
          </motion.form>
      </div>
    </section>
  );
};

export default CTASection;
